import { computed, ref } from 'vue'
import type { Policy as ApiPolicy } from 'policies-api'
import type { PolicyWithIconAndTitles } from 'de-zurich-ui-library'
import { isActivePolicyStatus } from '@/utils/policy/PolicyUtils'
import { navigate } from '@/utils/NavigationUtils'
import { PolicyList } from '@/utils/policy/PolicyList'
import { getToken } from '@/utils/KeycloakUtils'

export interface ApiRequestConfig {
  serviceUrl: string
  apiKey: string
  auth: string
  partnerProxyId: string | null
}

export function formAbortActionToServicePage(): void {
  navigate(globalThis.ENV_CONFIG?.REDIRECTS.CONTRACT_SERVICE_PAGE)
}

export function formAbortActionToPersonalProfil(): void {
  navigate(globalThis.ENV_CONFIG?.REDIRECTS.PERSONAL_PROFIL_PAGE)
}

export function goToOnlineFormularLink(): void {
  navigate(globalThis.ENV_CONFIG?.REDIRECTS.ACCOMPANIED_DRIVING_FORMULAR)
}

export async function apiRequestConfig(
  partnerProxyId: string
): Promise<ApiRequestConfig> {
  return {
    serviceUrl: globalThis.ENV_CONFIG?.SERVICE_HOSTS.POLICIES,
    apiKey: globalThis.ENV_CONFIG?.API_KEY,
    auth: `Bearer ${await getToken()}`,
    partnerProxyId
  }
}

/**
 * Composable für Self-Service-Listen-Komponenten.
 * Kapselt den gemeinsamen Zustand und die Filterlogik für Policen.
 *
 * @param loader - Asynchrone Funktion zum Laden der Policen-Liste
 */
export function useSelfServicePolicyList(
  loader: () => Promise<ApiPolicy[] | PolicyWithIconAndTitles[]>
) {
  const staticPolicies = ref<ApiPolicy[] | PolicyWithIconAndTitles[] | null>(null)
  const formLoading = ref(true)
  const showErrorTile = ref(false)
  const selectedPolicies = ref<ApiPolicy[]>([])
  const blackListedPolicyIDs: Set<string> = new Set<string>()

  const staticPoliciesFiltered = computed<PolicyWithIconAndTitles[]>(() => {
    if (!staticPolicies.value || staticPolicies.value.length === 0) return []
    return staticPolicies.value.filter((policy) =>
      isActivePolicyStatus(policy.policyStatus)
    ) as PolicyWithIconAndTitles[]
  })

  /**
   * Lädt die Policen-Liste und gibt die gefilterten aktiven Policen zurück.
   */
  async function loadPolicies(): Promise<PolicyWithIconAndTitles[]> {
    staticPolicies.value = await loader()
    return staticPoliciesFiltered.value
  }

  function setFormLoading(value: boolean): void {
    formLoading.value = value
  }

  function setShowErrorTile(value: boolean): void {
    showErrorTile.value = value
  }

  function setSelectedPolicies(policies: ApiPolicy[]): void {
    selectedPolicies.value = policies
  }

  function modifyPolicy(policy: ApiPolicy): ApiPolicy {
    return PolicyList.modifyPolicy(policy)
  }

  function checkPolicyOverview(policy: ApiPolicy): boolean {
    return PolicyList.checkPolicyOverview(policy, blackListedPolicyIDs)
  }

  function checkPolicyDetails(policy: ApiPolicy): boolean {
    return PolicyList.checkPolicyDetails(policy)
  }

  function checkPoliciesDetails(policies: ApiPolicy[]): boolean {
    return PolicyList.checkPoliciesDetails(policies, blackListedPolicyIDs)
  }

  return {
    apiRequestConfig,
    blackListedPolicyIDs,
    checkPoliciesDetails,
    checkPolicyDetails,
    checkPolicyOverview,
    formAbortActionToPersonalProfil,
    formAbortActionToServicePage,
    formLoading,
    goToOnlineFormularLink,
    loadPolicies,
    modifyPolicy,
    selectedPolicies,
    setFormLoading,
    setSelectedPolicies,
    setShowErrorTile,
    showErrorTile,
    staticPolicies,
    staticPoliciesFiltered
  }
}
